/**
 * The ThinkingCap mascot, live — public/models/thinking-cap.glb, vendored
 * from the canonical thinking-cap/TC_3D repo (THINKING_Cap.glb; springs +
 * gems + glowing bulb, matches the brand logo exactly). Reacts to:
 *  - idle: every baked spring/gem/body clip loops together (each clip only
 *    moves one part -- see the GLB inspection notes -- so they all need to
 *    play at once for the "boinging" effect to read as one).
 *  - pointer move: the whole cap tilts to look toward the cursor.
 *  - `thinking` prop (AI generating a reply): animation speeds up and the
 *    bulb's emissive glow intensifies, so it visibly "thinks harder."
 *  - click, OR `pulseKey` changing (the caller sent a message): a snappy
 *    scale-up/settle bounce, decaying back to rest. (Click no longer resizes
 *    the cap -- it always renders at its full `size`.)
 */
import { Suspense, useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useGLTF, useAnimations } from '@react-three/drei'
import * as THREE from 'three'

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

// The thinking-cap.glb authors its decorated FRONT (bulb centered, spring-loops
// fanning out, gems along the rim) facing +X, so at the camera's default view it
// showed the plain back of the dome -- i.e. "not facing the user." A fixed +90°
// yaw turns that front toward the camera. Applied on a static wrapper (below)
// so the per-frame cursor lean on `outer` composes on top of it rather than
// overwriting it. (Verified by headless render sweep: rx=0, ry=90 is the pose
// where the bulb is centered+upright and the springs/gems read symmetrically.)
const BASE_YAW = Math.PI / 2

function CapModel({ thinking, pulseKey, onClick }: { thinking: boolean; pulseKey: number; onClick: () => void }) {
  // outer = interactive transform (rotation toward cursor, click bounce);
  // inner = one-time fit/recenter, so rotation always pivots around the
  // model's actual center regardless of how Blender exported its origin.
  const outer = useRef<THREE.Group>(null)
  const inner = useRef<THREE.Group>(null)
  const { scene, animations } = useGLTF('/models/thinking-cap.glb')
  const { actions, mixer } = useAnimations(animations, inner)
  const { gl } = useThree()
  const pointer = useRef({ x: 0, y: 0 })
  const bounce = useRef(0)

  useEffect(() => {
    if (!inner.current) return
    const box = new THREE.Box3().setFromObject(scene)
    const dims = new THREE.Vector3()
    const center = new THREE.Vector3()
    box.getSize(dims)
    box.getCenter(center)
    const maxDim = Math.max(dims.x, dims.y, dims.z) || 1
    const fit = 1.7 / maxDim
    inner.current.scale.setScalar(fit)
    inner.current.position.set(-center.x * fit, -center.y * fit, -center.z * fit)
  }, [scene])

  // Every clip only animates ONE spring or gem -- play them all together,
  // looping, so the full fleet of springs/gems boings in unison.
  useEffect(() => {
    Object.values(actions).forEach((action) => {
      if (!action) return
      action.reset().play()
      action.setLoop(THREE.LoopRepeat, Infinity)
    })
  }, [actions])

  // Speed up the whole mixer while "thinking" -- the springs jiggle
  // visibly harder, reading as effort rather than idle bobbing.
  useEffect(() => {
    mixer.timeScale = thinking ? 2.6 : 1
  }, [thinking, mixer])

  // Find the bulb's material once, so clicking/thinking can drive its glow
  // without re-traversing the scene graph every frame.
  const bulbMaterials = useMemo(() => {
    const mats: THREE.MeshStandardMaterial[] = []
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      const name = `${obj.name} ${obj.parent?.name ?? ''}`.toLowerCase()
      if (name.includes('bulb') || name.includes('light')) {
        const mat = obj.material
        if (mat instanceof THREE.MeshStandardMaterial) mats.push(mat)
      }
    })
    return mats
  }, [scene])

  // Caller (Chat.tsx) bumps pulseKey the instant a message is sent -- an
  // immediate reaction to the send action itself, distinct from the
  // slower thinking-speedup that follows while the reply streams back.
  const firstPulse = useRef(true)
  useEffect(() => {
    if (firstPulse.current) { firstPulse.current = false; return }
    bounce.current = 0.4
  }, [pulseKey])

  // Tilt is relative to the mascot's OWN on-screen position, not raw window
  // coordinates -- distance-based and clamped to [-1,1], so it's zero (dead
  // ahead, facing the viewer) whenever the cursor isn't actually nearby,
  // and only leans in as the cursor approaches. TRACK_RADIUS is how close
  // (in px) the cursor needs to be before the cap starts noticing it.
  const TRACK_RADIUS = 220
  useEffect(() => {
    function onMove(e: PointerEvent) {
      const rect = gl.domElement.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      pointer.current = {
        x: clamp((e.clientX - cx) / TRACK_RADIUS, -1, 1),
        y: clamp((e.clientY - cy) / TRACK_RADIUS, -1, 1),
      }
    }
    window.addEventListener('pointermove', onMove)
    return () => window.removeEventListener('pointermove', onMove)
  }, [gl])

  useFrame((_, delta) => {
    if (!outer.current) return
    // Look toward the cursor -- gentle, capped rotation, not a literal aim.
    const targetY = pointer.current.x * 0.3
    const targetX = -pointer.current.y * 0.16
    outer.current.rotation.y += (targetY - outer.current.rotation.y) * 0.08
    outer.current.rotation.x += (targetX - outer.current.rotation.x) * 0.08

    // Click bounce: an impulse that decays back to zero each frame.
    bounce.current *= Math.max(0, 1 - delta * 6)
    outer.current.scale.setScalar(1 + bounce.current)

    const glow = thinking ? 1.8 + Math.sin(performance.now() / 120) * 0.6 : 0.6
    for (const mat of bulbMaterials) {
      mat.emissiveIntensity = glow
      if (mat.emissive.getHex() === 0x000000) mat.emissive.set(0xffcc55)
    }
  })

  function handleClick() {
    bounce.current = 0.35
    onClick()
  }

  return (
    <group ref={outer} onClick={handleClick}>
      {/* Fixed orientation so the model's front faces the camera; the cursor
          lean/bounce on `outer` above composes on top of this. */}
      <group rotation={[0, BASE_YAW, 0]}>
        <group ref={inner}>
          <primitive object={scene} />
        </group>
      </group>
    </group>
  )
}

export default function ThinkingCap3D({
  thinking = false, size = 56, pulseKey = 0,
}: { thinking?: boolean; size?: number; pulseKey?: number }) {
  // Always render at full `size` -- the old click-to-toggle small/large
  // behaviour is gone; click just triggers the bounce (handled in CapModel).
  //
  // The canvas is rendered LARGER than the cap's visual footprint (PAD) with the
  // camera pulled back to match, so the springs/antenna have headroom when the
  // cap floats up or bounce-scales -- otherwise they clip at the canvas edge and
  // "go out of frame". Layout still reserves only `size`; the extra canvas is
  // transparent, centered, and overflows invisibly. Camera z scales with PAD so
  // the cap's on-screen size is unchanged (model fills 1/PAD of the wider view).
  const PAD = 1.7
  const canvasPx = size * PAD
  return (
    <div style={{ width: size, height: size, position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
      <div
        style={{
          position: 'absolute', top: '50%', left: '50%',
          width: canvasPx, height: canvasPx, transform: 'translate(-50%, -50%)',
        }}
      >
      <Canvas
        camera={{ position: [0, 0, 3.2 * PAD], fov: 30 }}
        dpr={[1, 2]}
        gl={{ alpha: true, antialias: true, toneMappingExposure: 1.5 }}
      >
        {/* Brightened lighting -- the model read as very dark before. Strong
            ambient + hemisphere fill so no face is left in shadow, plus a
            key light and a camera-facing fill so the front is well lit. */}
        <ambientLight intensity={1.9} />
        <hemisphereLight args={[0xffffff, 0x444455, 1.2]} />
        <directionalLight position={[2, 3, 4]} intensity={2.6} />
        <directionalLight position={[-2, -1, -3]} intensity={1.1} />
        <directionalLight position={[0, 0, 5]} intensity={1.4} />
        <Suspense fallback={null}>
          <CapModel thinking={thinking} pulseKey={pulseKey} onClick={() => {}} />
        </Suspense>
      </Canvas>
      </div>
    </div>
  )
}
